import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, MessageSquare, Bot, CreditCard, Globe, Bell,
  Save, RefreshCw, Key, Link2, ShieldAlert, CheckCircle, XCircle, ExternalLink, Eye, EyeOff, Info,
  Monitor, Phone, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';
import DemoModeToggleCard from '../../components/DemoModeToggleCard';

const SettingsPage = () => {
  const { token, user, fetchUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [settingsAccess, setSettingsAccess] = useState({ whatsapp: false, ai: true, integrations: false });
  
  // Form states
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState('');
  const [industryCustomText, setIndustryCustomText] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  
  // Stripe integration state
  const [stripeSettings, setStripeSettings] = useState(null);
  const [testingStripe, setTestingStripe] = useState(false);
  const [stripeTestResult, setStripeTestResult] = useState(null);
  
  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    desktop_notifications_enabled: true,
    desktop_new_conversation: true,
    desktop_customer_message: true,
    desktop_conversation_escalated: true,
    email_notifications: true,
    message_alerts: true,
    workflow_alerts: true,
    billing_alerts: true,
  });
  const [notifSaving, setNotifSaving] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  
  // Industry options
  const industryOptions = [
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'retail', label: 'Retail' },
    { value: 'education', label: 'Education' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'professional_services', label: 'Professional Services' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'financial_services', label: 'Financial Services' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ];
  
  // AI config
  const [aiModel, setAiModel] = useState('gpt-5.2');
  const [aiEnabled, setAiEnabled] = useState(true);
  const [humanInLoop, setHumanInLoop] = useState(true);
  
  // Check if user is operator (restricted access)
  const isOperator = user?.role === 'operator' && !user?.is_super_admin;

  useEffect(() => {
    fetchOrganization();
    fetchSettingsAccess();
    fetchStripeSettings();
    fetchNotificationPreferences();
  }, []);

  const fetchSettingsAccess = async () => {
    try {
      const response = await axios.get(`${API}/settings/access-level`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettingsAccess(response.data);
    } catch (error) {
      console.error('Failed to fetch settings access:', error);
    }
  };

  const fetchStripeSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/integrations/stripe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeSettings(response.data);
    } catch (error) {
      // User may not have access to integration settings
      console.log('Stripe settings not accessible:', error.response?.status);
    }
  };

  const testStripeConnection = async () => {
    setTestingStripe(true);
    setStripeTestResult(null);
    try {
      const response = await axios.post(`${API}/settings/integrations/stripe/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeTestResult(response.data);
      if (response.data.success) {
        toast.success('Stripe connection successful!');
      } else {
        toast.error(response.data.error || 'Stripe connection failed');
      }
    } catch (error) {
      toast.error('Failed to test Stripe connection');
      setStripeTestResult({ success: false, error: 'Connection test failed' });
    } finally {
      setTestingStripe(false);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const response = await axios.get(`${API}/settings/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifPrefs(response.data);
    } catch (error) {
      console.log('Notification preferences not accessible:', error.response?.status);
    }
  };

  const updateNotifPref = useCallback(async (key, value) => {
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    
    try {
      await axios.put(`${API}/settings/notification-preferences`, updated, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      toast.error('Failed to save notification preference');
      setNotifPrefs(prev => ({ ...prev, [key]: !value }));
    }
  }, [notifPrefs, token]);

  const requestBrowserPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast.error('Browser notifications are not supported');
      return;
    }
    const result = await Notification.requestPermission();
    setBrowserPermission(result);
    if (result === 'granted') {
      toast.success('Desktop notifications enabled!');
    } else if (result === 'denied') {
      toast.error('Notification permission denied. Enable in browser settings.');
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/organizations/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const org = response.data;
      setOrganization(org);
      setOrgName(org.name);
      setIndustry(org.industry || '');
      setIndustryCustomText(org.industry_custom_text || '');
      setDefaultLanguage(org.default_language);
      // WhatsApp config moved to Channels page - no longer in Settings
      setAiModel(org.ai_config?.model || 'gpt-5.2');
      setAiEnabled(org.ai_config?.enabled !== false);
      setHumanInLoop(org.ai_config?.human_in_loop !== false);
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    if (isOperator) {
      toast.error('You do not have permission to modify settings');
      return;
    }
    
    // Validate industry custom text requirement
    if (industry === 'other' && !industryCustomText.trim()) {
      toast.error('Please describe your business industry when selecting "Other"');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/organizations/current`, {
        name: orgName,
        industry,
        industry_custom_text: industry === 'other' ? industryCustomText : null,
        default_language: defaultLanguage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved');
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAI = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/organizations/current`, {
        ai_config: {
          model: aiModel,
          provider: 'openai',
          enabled: aiEnabled,
          human_in_loop: humanInLoop
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('AI settings saved');
      fetchOrganization();
    } catch (error) {
      toast.error('Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your organization and integration settings</p>
      </div>

      {/* Demo Mode — secondary access point */}
      <DemoModeToggleCard variant="inline" />
      
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className={`grid w-full ${settingsAccess.integrations ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">AI Settings</TabsTrigger>
          {settingsAccess.integrations && (
            <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          )}
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
        </TabsList>
        
        {/* Operator notice */}
        {isOperator && (
          <Alert>
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              As an operator, you can view AI settings but cannot modify organization settings. Contact your admin for changes.
            </AlertDescription>
          </Alert>
        )}
        
        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organization Settings
              </CardTitle>
              <CardDescription>Update your organization details and preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    disabled={isOperator}
                    data-testid="org-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">
                    Industry <span className="text-red-500">*</span>
                  </Label>
                  <Select value={industry} onValueChange={setIndustry} disabled={isOperator}>
                    <SelectTrigger data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industryOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    AI responses will be tailored to your industry
                  </p>
                </div>
              </div>
              
              {/* Custom Industry Description - only shown when "Other" is selected */}
              {industry === 'other' && (
                <div className="space-y-2">
                  <Label htmlFor="industryCustomText">
                    Describe Your Business <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="industryCustomText"
                    value={industryCustomText}
                    onChange={(e) => setIndustryCustomText(e.target.value)}
                    placeholder="Describe your business industry and services (e.g., 'We provide pet grooming and veterinary services')"
                    disabled={isOperator}
                    className="min-h-[80px]"
                    data-testid="industry-custom-text"
                  />
                  <p className="text-xs text-muted-foreground">
                    This description helps AI understand your business and provide relevant responses
                  </p>
                </div>
              )}
              
              {/* Industry Alert */}
              {industry && (
                <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
                  <Bot className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 dark:text-blue-200">
                    <strong>Industry AI Guardrails Active:</strong> AI will only answer questions relevant to{' '}
                    {industry === 'other' ? 'your described business' : industryOptions.find(o => o.value === industry)?.label || 'your industry'}.
                    Off-topic questions will be politely redirected.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="language">Default Language</Label>
                <Select value={defaultLanguage} onValueChange={setDefaultLanguage} disabled={isOperator}>
                  <SelectTrigger className="w-48" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {!isOperator && (
                <div className="pt-4">
                  <Button 
                    onClick={handleSaveGeneral} 
                    disabled={saving}
                    className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="save-general-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* AI Settings */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5" />
                AI Configuration
              </CardTitle>
              <CardDescription>Configure AI-powered message processing and suggestions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable AI Assistance</Label>
                  <p className="text-sm text-muted-foreground">Allow AI to process messages and suggest replies</p>
                </div>
                <Switch
                  checked={aiEnabled}
                  onCheckedChange={setAiEnabled}
                  data-testid="ai-enabled-switch"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Human-in-the-Loop</Label>
                  <p className="text-sm text-muted-foreground">Require human approval before sending AI responses</p>
                </div>
                <Switch
                  checked={humanInLoop}
                  onCheckedChange={setHumanInLoop}
                  data-testid="human-loop-switch"
                />
              </div>
              
              <div className="space-y-2">
                <Label>AI Model</Label>
                <Select value={aiModel} onValueChange={setAiModel}>
                  <SelectTrigger data-testid="ai-model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-5.2">GPT-5.2 (Recommended)</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    <SelectItem value="claude-sonnet">Claude Sonnet 4.5</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-4 bg-lime-50 dark:bg-lime-900/20 rounded-lg border border-lime-200 dark:border-lime-800">
                <p className="text-sm text-lime-800 dark:text-lime-300">
                  <strong>Note:</strong> AI features use the Emergent LLM key for processing. 
                  Usage is tracked and billed based on your subscription plan.
                </p>
              </div>
              
              <div className="pt-4">
                <Button 
                  onClick={handleSaveAI} 
                  disabled={saving}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="save-ai-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save AI Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Integrations - Owner/Admin Only */}
        {settingsAccess.integrations && (
          <TabsContent value="integrations">
            <div className="space-y-6">
              {/* Stripe Integration Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Stripe Payment Integration
                  </CardTitle>
                  <CardDescription>
                    Configure Stripe for payment processing and subscriptions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {stripeSettings ? (
                    <>
                      {/* Configuration Status */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className={`p-4 rounded-lg border ${stripeSettings.status.api_key_configured ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {stripeSettings.status.api_key_configured ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span className="font-medium text-sm">API Key</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {stripeSettings.status.api_key_configured ? 'Configured' : 'Not configured'}
                          </p>
                        </div>
                        
                        <div className={`p-4 rounded-lg border ${stripeSettings.status.webhook_secret_configured ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {stripeSettings.status.webhook_secret_configured ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <ShieldAlert className="w-4 h-4 text-amber-600" />
                            )}
                            <span className="font-medium text-sm">Webhook Secret</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {stripeSettings.status.webhook_secret_configured ? 'Verified' : 'Not configured'}
                          </p>
                        </div>
                        
                        <div className={`p-4 rounded-lg border ${stripeSettings.status.production_ready ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-slate-50 border-slate-200 dark:bg-slate-900/20 dark:border-slate-800'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {stripeSettings.status.production_ready ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-slate-400" />
                            )}
                            <span className="font-medium text-sm">Production Ready</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {stripeSettings.status.production_ready ? 'Ready' : 'Incomplete setup'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Webhook URL */}
                      <div className="space-y-2">
                        <Label>Webhook Endpoint URL</Label>
                        <div className="flex items-center gap-2">
                          <Input 
                            value={`${window.location.origin}${stripeSettings.webhook_url}`}
                            readOnly
                            className="font-mono text-sm bg-slate-50 dark:bg-slate-900"
                            data-testid="stripe-webhook-url"
                          />
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}${stripeSettings.webhook_url}`);
                              toast.success('Webhook URL copied!');
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Add this URL to your Stripe Dashboard → Developers → Webhooks
                        </p>
                      </div>
                      
                      {/* Webhook Events */}
                      <div className="space-y-2">
                        <Label>Required Webhook Events</Label>
                        <div className="flex flex-wrap gap-2">
                          {stripeSettings.instructions.webhook_events.map((event) => (
                            <span 
                              key={event}
                              className="px-2 py-1 text-xs font-mono bg-slate-100 dark:bg-slate-800 rounded"
                            >
                              {event}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Environment Variables Notice */}
                      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                        <Key className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800 dark:text-blue-300">
                          <strong>Environment Variables Required:</strong>
                          <ul className="mt-2 space-y-1 text-sm">
                            <li><code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">STRIPE_API_KEY</code> - Your Stripe secret key (sk_live_... or sk_test_...)</li>
                            <li><code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">STRIPE_WEBHOOK_SECRET</code> - Webhook signing secret (whsec_...)</li>
                          </ul>
                          <p className="mt-2 text-xs">
                            Set these in Emergent Deployments → Environment Variables
                          </p>
                        </AlertDescription>
                      </Alert>
                      
                      {/* Test Connection */}
                      <div className="pt-4 flex items-center gap-4">
                        <Button 
                          onClick={testStripeConnection}
                          disabled={testingStripe}
                          variant="outline"
                          data-testid="test-stripe-btn"
                        >
                          {testingStripe ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <Link2 className="w-4 h-4 mr-2" />
                              Test Connection
                            </>
                          )}
                        </Button>
                        
                        <a 
                          href="https://dashboard.stripe.com/webhooks" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-emerald-700 hover:underline flex items-center gap-1"
                        >
                          Open Stripe Dashboard
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      
                      {/* Test Result */}
                      {stripeTestResult && (
                        <div className={`p-4 rounded-lg border ${stripeTestResult.success ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                          {stripeTestResult.success ? (
                            <>
                              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium">
                                <CheckCircle className="w-4 h-4" />
                                Connection Successful
                              </div>
                              <div className="mt-2 text-sm text-emerald-600 dark:text-emerald-300">
                                <p>Mode: <strong>{stripeTestResult.mode}</strong></p>
                                {stripeTestResult.account && (
                                  <p>Account: {stripeTestResult.account.id}</p>
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-medium">
                                <XCircle className="w-4 h-4" />
                                {stripeTestResult.error}
                              </div>
                              {stripeTestResult.details && (
                                <p className="mt-1 text-sm text-red-600 dark:text-red-300">{stripeTestResult.details}</p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Current Organization Stripe Status */}
                      {stripeSettings.org_config.customer_id && (
                        <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                          <h4 className="font-medium mb-2">Organization Stripe Status</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Customer ID:</span>
                              <p className="font-mono">{stripeSettings.org_config.customer_id}</p>
                            </div>
                            {stripeSettings.org_config.subscription_id && (
                              <div>
                                <span className="text-muted-foreground">Subscription:</span>
                                <p className="font-mono">{stripeSettings.org_config.subscription_id}</p>
                              </div>
                            )}
                            <div>
                              <span className="text-muted-foreground">Status:</span>
                              <p className={`font-medium ${stripeSettings.org_config.subscription_status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {stripeSettings.org_config.subscription_status}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Unable to load Stripe settings</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Setup Instructions Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Setup Instructions</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3 text-sm">
                    {stripeSettings?.instructions.setup_steps.map((step, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="text-muted-foreground">{step.replace(/^\d+\.\s*/, '')}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
        
        {/* Notifications */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Preferences
              </CardTitle>
              <CardDescription>Configure how you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Desktop Notifications Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  <Monitor className="w-4 h-4" />
                  Desktop Notifications
                </div>

                {/* Browser Permission Status */}
                {browserPermission !== 'granted' && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-amber-700">
                        {browserPermission === 'denied' 
                          ? 'Browser notifications blocked. Enable in browser settings.' 
                          : 'Browser notification permission required'}
                      </span>
                    </div>
                    {browserPermission === 'default' && (
                      <Button size="sm" variant="outline" onClick={requestBrowserPermission} data-testid="request-permission-btn">
                        Enable
                      </Button>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Desktop Notifications</Label>
                    <p className="text-sm text-muted-foreground">Show browser notifications when tab is in background</p>
                  </div>
                  <Switch 
                    checked={notifPrefs.desktop_notifications_enabled} 
                    onCheckedChange={(v) => updateNotifPref('desktop_notifications_enabled', v)}
                    data-testid="desktop-notif-master-switch"
                  />
                </div>

                {notifPrefs.desktop_notifications_enabled && (
                  <div className="pl-4 border-l-2 border-muted space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-green-500" />
                        <div>
                          <Label>New Conversations</Label>
                          <p className="text-sm text-muted-foreground">When a new customer starts a conversation</p>
                        </div>
                      </div>
                      <Switch 
                        checked={notifPrefs.desktop_new_conversation} 
                        onCheckedChange={(v) => updateNotifPref('desktop_new_conversation', v)}
                        data-testid="desktop-new-conv-switch"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-blue-500" />
                        <div>
                          <Label>Customer Messages</Label>
                          <p className="text-sm text-muted-foreground">When a customer sends a new message</p>
                        </div>
                      </div>
                      <Switch 
                        checked={notifPrefs.desktop_customer_message} 
                        onCheckedChange={(v) => updateNotifPref('desktop_customer_message', v)}
                        data-testid="desktop-customer-msg-switch"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <div>
                          <Label>Escalations</Label>
                          <p className="text-sm text-muted-foreground">When AI escalates to a human operator</p>
                        </div>
                      </div>
                      <Switch 
                        checked={notifPrefs.desktop_conversation_escalated} 
                        onCheckedChange={(v) => updateNotifPref('desktop_conversation_escalated', v)}
                        data-testid="desktop-escalation-switch"
                      />
                    </div>
                  </div>
                )}
              </div>

              <hr />

              {/* In-App Notifications Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  <Bell className="w-4 h-4" />
                  In-App & Email Notifications
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">Receive important updates via email</p>
                  </div>
                  <Switch 
                    checked={notifPrefs.email_notifications} 
                    onCheckedChange={(v) => updateNotifPref('email_notifications', v)} 
                    data-testid="email-notif-switch" 
                  />
                </div>
              
                <div className="flex items-center justify-between">
                  <div>
                    <Label>New Message Alerts</Label>
                    <p className="text-sm text-muted-foreground">Get notified when new messages arrive</p>
                  </div>
                  <Switch 
                    checked={notifPrefs.message_alerts} 
                    onCheckedChange={(v) => updateNotifPref('message_alerts', v)}
                    data-testid="message-notif-switch" 
                  />
                </div>
              
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Workflow Alerts</Label>
                    <p className="text-sm text-muted-foreground">Notify when automation workflows fail</p>
                  </div>
                  <Switch 
                    checked={notifPrefs.workflow_alerts} 
                    onCheckedChange={(v) => updateNotifPref('workflow_alerts', v)}
                    data-testid="workflow-notif-switch" 
                  />
                </div>
              
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Billing Alerts</Label>
                    <p className="text-sm text-muted-foreground">Receive payment and billing notifications</p>
                  </div>
                  <Switch 
                    checked={notifPrefs.billing_alerts} 
                    onCheckedChange={(v) => updateNotifPref('billing_alerts', v)}
                    data-testid="billing-notif-switch" 
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
